import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from "react";

interface SearchState {
  cityName: string;
  arrivingDate: Date | null;
  departureDate: Date | null;
  numberOfAdults: number;
  numberOfChildren: number;
  withPets: boolean;
}

type SearchAction =
  | { type: "SET_CITY_NAME"; payload: string }
  | { type: "SET_ARRIVING_DATE"; payload: Date | null }
  | { type: "SET_DEPARTURE_DATE"; payload: Date | null }
  | { type: "SET_NUMBER_OF_ADULTS"; payload: number }
  | { type: "SET_NUMBER_OF_CHILDREN"; payload: number }
  | { type: "TOGGLE_PETS" }
  | { type: "RESET" }
  | { type: "SET_STATE"; payload: Partial<SearchState> };

const initialSearchState: SearchState = {
  cityName: "",
  arrivingDate: null,
  departureDate: null,
  numberOfAdults: 1,
  numberOfChildren: 0,
  withPets: false,
};

const SearchContext = createContext<SearchState | undefined>(undefined);
const SearchDispatchContext = createContext<
  React.Dispatch<SearchAction> | undefined
>(undefined);

const searchReducer = (
  state: SearchState,
  action: SearchAction
): SearchState => {
  switch (action.type) {
    case "SET_CITY_NAME":
      return { ...state, cityName: action.payload };
    case "SET_ARRIVING_DATE":
      return { ...state, arrivingDate: action.payload };
    case "SET_DEPARTURE_DATE":
      return { ...state, departureDate: action.payload };
    case "SET_NUMBER_OF_ADULTS":
      return { ...state, numberOfAdults: action.payload };
    case "SET_NUMBER_OF_CHILDREN":
      return { ...state, numberOfChildren: action.payload };
    case "TOGGLE_PETS":
      return { ...state, withPets: !state.withPets };
    case "RESET":
      return initialSearchState;
    case "SET_STATE": // New case to restore state
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(searchReducer, initialSearchState);

  // Restore state from localStorage on component mount
  useEffect(() => {
    const savedState = JSON.parse(localStorage.getItem("searchState") || "{}");
    if (savedState) {
      dispatch({ type: "SET_STATE", payload: savedState });
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("searchState", JSON.stringify(state));
  }, [state]);

  return (
    <SearchContext.Provider value={state}>
      <SearchDispatchContext.Provider value={dispatch}>
        {children}
      </SearchDispatchContext.Provider>
    </SearchContext.Provider>
  );
};

export const useSearchState = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearchState must be used within a SearchProvider");
  }
  return context;
};

export const useSearchDispatch = () => {
  const context = useContext(SearchDispatchContext);
  if (context === undefined) {
    throw new Error("useSearchDispatch must be used within a SearchProvider");
  }
  return context;
};
